import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, ShoppingCart, Package, Store, Layers, Receipt, Warehouse,
  Wallet, Shield, LogOut, Menu, ArrowLeftRight, FileBarChart2, Scale, Building2,
  ShoppingBag, FileSpreadsheet, Tag, UserCheck, BookOpen, TrendingUp, Settings2,
  PackagePlus, PackageMinus, ClipboardList,
  ChevronDown, X, CircleHelp, Search, Activity,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import WorkspaceTabs from "@/components/WorkspaceTabs";
import PersistentWorkspaceOutlet from "@/components/PersistentWorkspaceOutlet";
import {
  createWorkspaceTab,
  removeWorkspaceTab,
  updateWorkspaceTabPath
} from "@/lib/workspaceTabState";
import { useRealtime } from "@/lib/ws";
import { toast } from "sonner";


// STAGE 1: submenu blueprint untuk halaman yang belum diimplementasikan.
const plannedItems = (entries) => entries.map(([slug, label]) => ({
  to: `/planned/${slug}?title=${encodeURIComponent(label)}&status=Direncanakan`,
  label,
  status: "Direncanakan",
  icon: ClipboardList,
}));

/* STAGE1_BLUEPRINT_MENU_EXPANSION */
const NAV_GROUPS = [
  { label: "Persiapan Data", items: [
      ...plannedItems([
      ["validasi-data-perusahaan", "Validasi Data Perusahaan"],
      ["pengaturan-struktur-bisnis", "Pengaturan Struktur Bisnis"],
      ["pengaturan-master-awal", "Pengaturan Master Awal"],
      ["saldo-awal-perusahaan", "Saldo Awal Perusahaan"],
      ["checklist-kesiapan-operasional", "Checklist Kesiapan Operasional"]
      ]),
    { to: "/company-setup", label: "Persiapan Data Perusahaan", icon: Building2, roles: ["owner"] },
  ]},
  { label: "Pusat Informasi", items: [
      ...plannedItems([
      ["approval-saya", "Approval Saya"],
      ["pengajuan-saya", "Pengajuan Saya"],
      ["memo-masuk", "Memo Masuk"],
      ["memo-terkirim", "Memo Terkirim"],
      ["disposisi-saya", "Disposisi Saya"],
      ["tugas-dan-tindak-lanjut", "Tugas dan Tindak Lanjut"],
      ["riwayat-approval-dan-keputusan", "Riwayat Approval dan Keputusan"],
      ["notifikasi-perusahaan", "Notifikasi Perusahaan"]
      ]),
    { to: "/dashboard", label: "Dashboard Perusahaan", icon: LayoutDashboard, roles: ["owner", "manager", "supervisor", "keuangan"] },
    { to: "/command-center", label: "Alert dan Keputusan Manajemen", icon: TrendingUp, roles: ["owner", "manager", "keuangan"] },
    { to: "/planned/kpi-dan-target-perusahaan-1?title=KPI+dan+Target+Perusahaan&status=Direncanakan", label: "KPI dan Target Perusahaan", status: "Direncanakan", icon: TrendingUp },
    { to: "/planned/performance-review-2?title=Performance+Review&status=Direncanakan", label: "Performance Review", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/evaluasi-kinerja-bisnis-3?title=Evaluasi+Kinerja+Bisnis&status=Direncanakan", label: "Evaluasi Kinerja Bisnis", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/ekspansi-bisnis-4?title=Ekspansi+Bisnis&status=Direncanakan", label: "Ekspansi Bisnis", status: "Direncanakan", icon: Building2 },
    { to: "/planned/studi-kelayakan-outlet-5?title=Studi+Kelayakan+Outlet&status=Direncanakan", label: "Studi Kelayakan Outlet", status: "Direncanakan", icon: Store },
    { to: "/planned/evaluasi-lokasi-baru-6?title=Evaluasi+Lokasi+Baru&status=Direncanakan", label: "Evaluasi Lokasi Baru", status: "Direncanakan", icon: Store },
  ]},
  { label: "POS Kasir", items: [
    { to: "/pos", label: "POS Kasir", icon: ShoppingCart, roles: ["owner", "manager", "supervisor", "kasir", "cashier_outlet"] },
  ]},
  { label: "Penjualan", items: [
      ...plannedItems([
      ["penawaran-penjualan", "Penawaran Penjualan"],
      ["faktur-penjualan", "Faktur Penjualan"],
      ["daftar-piutang-pelanggan", "Daftar Piutang Pelanggan"],
      ["monitoring-pesanan-penjualan", "Monitoring Pesanan Penjualan"],
      ["komisi-tenaga-penjual", "Komisi Tenaga Penjual"]
      ]),
    { to: "/transactions", label: "Transaksi Penjualan", icon: Receipt, roles: ["owner", "manager", "supervisor", "kasir", "cashier_outlet", "keuangan"] },
    { to: "/planned/pesanan-penjualan?title=Pesanan+Penjualan&status=Direncanakan", label: "Pesanan Penjualan", status: "Direncanakan", icon: ShoppingCart },
    { to: "/planned/pengiriman-penjualan?title=Pengiriman+Penjualan&status=Direncanakan", label: "Pengiriman Penjualan", status: "Direncanakan", icon: Package },
    { to: "/planned/retur-penjualan?title=Retur+Penjualan&status=Direncanakan", label: "Retur Penjualan", status: "Direncanakan", icon: Receipt },
    { to: "/planned/penerimaan-pembayaran-pelanggan?title=Penerimaan+Pembayaran+Pelanggan&status=Direncanakan", label: "Penerimaan Pembayaran Pelanggan", status: "Direncanakan", icon: Wallet },
    { to: "/planned/refund-dan-koreksi-transaksi-7?title=Refund+dan+Koreksi+Transaksi&status=Perlu+verifikasi", label: "Refund dan Koreksi Transaksi", status: "Perlu verifikasi", icon: Receipt },
    { to: "/planned/target-penjualan-outlet-8?title=Target+Penjualan+Outlet&status=Direncanakan", label: "Target Penjualan Outlet", status: "Direncanakan", icon: TrendingUp },
    { to: "/planned/kinerja-produk-dan-kanal-penjualan-9?title=Kinerja+Produk+dan+Kanal+Penjualan&status=Perlu+verifikasi", label: "Kinerja Produk dan Kanal Penjualan", status: "Perlu verifikasi", icon: FileBarChart2 },
    { to: "/planned/evaluasi-kinerja-outlet-10?title=Evaluasi+Kinerja+Outlet&status=Direncanakan", label: "Evaluasi Kinerja Outlet", status: "Direncanakan", icon: Store },
    { to: "/planned/pelanggan-dan-keluhan-11?title=Pelanggan+dan+Keluhan&status=Direncanakan", label: "Pelanggan dan Keluhan", status: "Direncanakan", icon: CircleHelp },
    { to: "/planned/tindak-lanjut-keluhan-12?title=Tindak+Lanjut+Keluhan&status=Direncanakan", label: "Tindak Lanjut Keluhan", status: "Direncanakan", icon: ClipboardList },
  ]},
  { label: "Pembelian", items: [
      ...plannedItems([
      ["persetujuan-pembelian", "Persetujuan Pembelian"],
      ["pemilihan-supplier", "Pemilihan Supplier"],
      ["perbandingan-harga-supplier", "Perbandingan Harga Supplier"],
      ["monitoring-pesanan-belum-diterima", "Monitoring Pesanan Belum Diterima"]
      ]),
    { to: "/planned/purchase-request-pr-13?title=Purchase+Request+%28PR%29&status=Direncanakan", label: "Purchase Request (PR)", status: "Direncanakan", icon: ClipboardList },
    { to: "/purchasing?tab=pos", label: "Purchase Order (PO)", icon: ShoppingBag, roles: ["owner", "manager", "gudang"] },
    { to: "/purchasing?tab=suppliers", label: "Master Supplier", icon: Building2, roles: ["owner", "manager", "gudang"] },
    { to: "/planned/permintaan-penawaran-supplier?title=Permintaan+Penawaran+Supplier&status=Direncanakan", label: "Permintaan Penawaran Supplier", status: "Direncanakan", icon: ClipboardList },
    { to: "/planned/penerimaan-barang?title=Penerimaan+Barang&status=Direncanakan", label: "Penerimaan Barang", status: "Direncanakan", icon: PackagePlus },
    { to: "/planned/tagihan-pembelian?title=Tagihan+Pembelian&status=Direncanakan", label: "Tagihan Pembelian", status: "Direncanakan", icon: Receipt },
    { to: "/planned/retur-pembelian?title=Retur+Pembelian&status=Direncanakan", label: "Retur Pembelian", status: "Direncanakan", icon: PackageMinus },
    { to: "/stock-in?tab=belanja", label: "Stock In — Belanja", icon: PackagePlus },
  ]},
  { label: "Persediaan", items: [
      ...plannedItems([
      ["dashboard-persediaan", "Dashboard Persediaan"],
      ["saldo-persediaan", "Saldo Persediaan"],
      ["permintaan-barang", "Permintaan Barang"],
      ["kartu-stok", "Kartu Stok"],
      ["stock-minimum", "Stock Minimum"],
      ["riwayat-mutasi-persediaan", "Riwayat Mutasi Persediaan"]
      ]),
    { to: "/stock-in?tab=transfer", label: "Stock In — Transfer", icon: PackagePlus },
    { to: "/stock-in?tab=manual", label: "Stock In — Manual", icon: PackagePlus },
    { to: "/stock-in?tab=riwayat", label: "Riwayat Stock In", icon: ClipboardList },
    { to: "/inventory?tab=ingredients", label: "Master Bahan Baku", icon: Package, roles: ["owner", "manager", "supervisor", "gudang"] },
    { to: "/inventory?tab=products", label: "Master Produk", icon: Package, roles: ["owner", "manager", "supervisor", "gudang"] },
    { to: "/inventory", label: "Inventory dan Saldo Stok", icon: Warehouse, roles: ["owner", "manager", "supervisor", "gudang"] },
    { to: "/inventory?tab=stock-ledger", label: "Stock Movement", icon: ClipboardList, roles: ["owner", "manager", "supervisor", "gudang"] },
    { to: "/stock-out", label: "Stock Out", icon: PackageMinus, roles: ["owner", "manager", "supervisor", "gudang"] },
    { to: "/transfers", label: "Stock Transfer", icon: ArrowLeftRight, roles: ["owner", "manager", "supervisor", "gudang"] },
    { to: "/stock-request", label: "Stock Request", icon: ClipboardList },
    { to: "/inventory?tab=opname", label: "Stock Opname", icon: ClipboardList, roles: ["owner", "manager", "supervisor", "gudang"] },
    { to: "/planned/stock-adjustment-14?title=Stock+Adjustment&status=Perlu+verifikasi", label: "Stock Adjustment", status: "Perlu verifikasi", icon: Package },
    { to: "/planned/waste-dan-barang-kedaluwarsa-15?title=Waste+dan+Barang+Kedaluwarsa&status=Perlu+verifikasi", label: "Waste dan Barang Kedaluwarsa", status: "Perlu verifikasi", icon: PackageMinus },
  ]},
  { label: "Pemasaran", items: [
      ...plannedItems([
      ["kategori-pelanggan", "Kategori Pelanggan"],
      ["campaign-marketing", "Campaign Marketing"],
      ["segmentasi-pelanggan", "Segmentasi Pelanggan"],
      ["loyalty-tier", "Loyalty Tier"],
      ["poin-dan-reward", "Poin dan Reward"],
      ["analisis-pelanggan", "Analisis Pelanggan"]
      ]),
    { to: "/planned/dashboard-marketing-30?title=Dashboard+Marketing&status=Direncanakan", label: "Dashboard Marketing", status: "Direncanakan", icon: TrendingUp },
    { to: "/planned/kalender-marketing-31?title=Kalender+Marketing&status=Direncanakan", label: "Kalender Marketing", status: "Direncanakan", icon: Activity },
    { to: "/planned/perencanaan-kampanye-32?title=Perencanaan+Kampanye&status=Direncanakan", label: "Perencanaan Kampanye", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/promos", label: "Promosi dan Diskon", icon: Tag, roles: ["owner", "manager"] },
    { to: "/customers", label: "Database Pelanggan", icon: UserCheck, roles: ["owner", "manager"] },
    { to: "/planned/program-loyalitas-34?title=Program+Loyalitas&status=Direncanakan", label: "Program Loyalitas", status: "Direncanakan", icon: TrendingUp },
    { to: "/planned/keluhan-dan-layanan-pelanggan-35?title=Keluhan+dan+Layanan+Pelanggan&status=Direncanakan", label: "Keluhan dan Layanan Pelanggan", status: "Direncanakan", icon: CircleHelp },
    { to: "/planned/tindak-lanjut-pelanggan-36?title=Tindak+Lanjut+Pelanggan&status=Direncanakan", label: "Tindak Lanjut Pelanggan", status: "Direncanakan", icon: ClipboardList },
    { to: "/planned/evaluasi-kampanye-37?title=Evaluasi+Kampanye&status=Direncanakan", label: "Evaluasi Kampanye", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/analisis-hasil-promosi-38?title=Analisis+Hasil+Promosi&status=Perlu+verifikasi", label: "Analisis Hasil Promosi", status: "Perlu verifikasi", icon: TrendingUp },
  ]},
  { label: "Produksi", items: [
      ...plannedItems([
      ["dashboard-produksi", "Dashboard Produksi"],
      ["resep-produk", "Resep Produk"],
      ["penyelesaian-produksi", "Penyelesaian Produksi"],
      ["perhitungan-hpp", "Perhitungan HPP"],
      ["riwayat-produksi", "Riwayat Produksi"]
      ]),
    { to: "/bom-hpp", label: "Barang & Jasa", icon: Layers, roles: ["owner", "manager"] },
    { to: "/planned/perintah-produksi?title=Perintah+Produksi&status=Direncanakan", label: "Perintah Produksi", status: "Direncanakan", icon: Layers },
    { to: "/planned/pemakaian-bahan-produksi?title=Pemakaian+Bahan+Produksi&status=Direncanakan", label: "Pemakaian Bahan Produksi", status: "Direncanakan", icon: PackageMinus },
    { to: "/planned/hasil-produksi?title=Hasil+Produksi&status=Direncanakan", label: "Hasil Produksi", status: "Direncanakan", icon: PackagePlus },
    { to: "/planned/standardisasi-produk-16?title=Standardisasi+Produk&status=Direncanakan", label: "Standardisasi Produk", status: "Direncanakan", icon: Package },
    { to: "/planned/pengujian-dan-pengembangan-produk-17?title=Pengujian+dan+Pengembangan+Produk&status=Direncanakan", label: "Pengujian dan Pengembangan Produk", status: "Direncanakan", icon: Layers },
  ]},
  { label: "Keuangan", items: [
      ...plannedItems([
      ["dashboard-keuangan", "Dashboard Keuangan"],
      ["posisi-kas", "Posisi Kas"],
      ["posisi-bank", "Posisi Bank"],
      ["penerimaan-kas", "Penerimaan Kas"],
      ["pengeluaran-kas", "Pengeluaran Kas"],
      ["transfer-kas-bank", "Transfer Kas/Bank"],
      ["daftar-piutang", "Daftar Piutang"],
      ["faktur-belum-lunas-piutang", "Faktur Belum Lunas Piutang"],
      ["penerimaan-piutang", "Penerimaan Piutang"],
      ["umur-piutang", "Umur Piutang"],
      ["daftar-utang", "Daftar Utang"],
      ["faktur-belum-lunas-utang", "Faktur Belum Lunas Utang"],
      ["umur-utang", "Umur Utang"],
      ["anggaran-perusahaan", "Anggaran Perusahaan"],
      ["anggaran-outlet", "Anggaran Outlet"],
      ["anggaran-departemen", "Anggaran Departemen"],
      ["realisasi-anggaran", "Realisasi Anggaran"],
      ["tutup-buku", "Tutup Buku"]
      ]),
    { to: "/cash-bank", label: "Kas dan Bank", icon: Wallet, roles: ["owner", "manager", "keuangan"] },
    { to: "/expenses", label: "Pencatatan Pengeluaran", icon: Wallet, roles: ["owner", "manager", "supervisor", "keuangan"] },
    { to: "/planned/budget-dan-realisasi-anggaran-19?title=Budget+dan+Realisasi+Anggaran&status=Direncanakan", label: "Budget dan Realisasi Anggaran", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/arus-kas-20?title=Arus+Kas&status=Perlu+verifikasi", label: "Arus Kas", status: "Perlu verifikasi", icon: TrendingUp },
    { to: "/planned/account-payable-ap-utang-usaha-21?title=Account+Payable+%28AP%29+%2F+Utang+Usaha&status=Perlu+verifikasi", label: "Account Payable (AP) / Utang Usaha", status: "Perlu verifikasi", icon: Receipt },
    { to: "/planned/pembayaran-supplier-22?title=Pembayaran+Supplier&status=Perlu+verifikasi", label: "Pembayaran Supplier", status: "Perlu verifikasi", icon: Wallet },
    { to: "/planned/uang-muka-pembelian?title=Uang+Muka+Pembelian&status=Direncanakan", label: "Uang Muka Pembelian", status: "Direncanakan", icon: Wallet },
    { to: "/planned/daftar-utang-supplier?title=Daftar+Utang+Supplier&status=Direncanakan", label: "Daftar Utang Supplier", status: "Direncanakan", icon: Receipt },
    { to: "/finance?tab=coa", label: "Chart of Accounts (COA)", icon: BookOpen, roles: ["owner", "manager", "keuangan"] },
    { to: "/finance?tab=journal", label: "Journal Entries", icon: FileSpreadsheet, roles: ["owner", "manager", "keuangan"] },
    { to: "/planned/general-ledger-23?title=General+Ledger&status=Direncanakan", label: "General Ledger", status: "Direncanakan", icon: BookOpen },
    { to: "/planned/daftar-akun?title=Daftar+Akun&status=Direncanakan", label: "Daftar Akun", status: "Direncanakan", icon: BookOpen },
    { to: "/planned/jurnal-umum?title=Jurnal+Umum&status=Direncanakan", label: "Jurnal Umum", status: "Direncanakan", icon: FileSpreadsheet },
    { to: "/planned/jurnal-penyesuaian?title=Jurnal+Penyesuaian&status=Direncanakan", label: "Jurnal Penyesuaian", status: "Direncanakan", icon: FileSpreadsheet },
    { to: "/planned/trial-balance-24?title=Trial+Balance&status=Direncanakan", label: "Trial Balance", status: "Direncanakan", icon: Scale },
    { to: "/planned/pengaturan-pajak?title=Pengaturan+Pajak&status=Direncanakan", label: "Pengaturan Pajak", status: "Direncanakan", icon: Receipt },
    { to: "/planned/laporan-pajak-masa?title=Laporan+Pajak+Masa&status=Direncanakan", label: "Laporan Pajak Masa", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/reconciliation", label: "Rekonsiliasi", icon: Scale, roles: ["owner", "manager", "keuangan"] },
    { to: "/planned/periode-akuntansi-27?title=Periode+Akuntansi&status=Direncanakan", label: "Periode Akuntansi", status: "Direncanakan", icon: Activity },
    { to: "/planned/koreksi-dan-pembalikan-jurnal-28?title=Koreksi+dan+Pembalikan+Jurnal&status=Direncanakan", label: "Koreksi dan Pembalikan Jurnal", status: "Direncanakan", icon: FileSpreadsheet },
    { to: "/sheets", label: "Google Sheets", icon: FileSpreadsheet, roles: ["owner", "manager", "keuangan"] },
  ]},
  { label: "Personalia", items: [
      ...plannedItems([
      ["dashboard-hr", "Dashboard HR"],
      ["departemen-dan-jabatan-hr", "Departemen dan Jabatan"],
      ["cuti-dan-izin", "Cuti dan Izin"],
      ["slip-gaji", "Slip Gaji"],
      ["tunjangan-dan-potongan", "Tunjangan dan Potongan"],
      ["riwayat-payroll", "Riwayat Payroll"]
      ]),
    { to: "/master-data?tab=employees", label: "Master Data Karyawan", icon: UserCheck, roles: ["owner"] },
    { to: "/hr?tab=attendance", label: "Absensi dan Kehadiran", icon: UserCheck, roles: ["owner", "manager", "supervisor"] },
    { to: "/hr?tab=overtime", label: "Lembur", icon: Activity, roles: ["owner", "manager", "supervisor"] },
    { to: "/hr?tab=leave", label: "Izin dan Manajemen Libur", icon: ClipboardList, roles: ["owner", "manager", "supervisor"] },
    { to: "/shifts", label: "Jadwal Kerja dan Shift", icon: Activity, roles: ["owner", "manager", "supervisor", "kasir", "cashier_outlet"] },
    { to: "/hr?tab=jobdesk", label: "Jobdesk dan Penugasan", icon: ClipboardList, roles: ["owner", "manager", "supervisor"] },
    { to: "/hr?tab=kpi", label: "KPI Karyawan", icon: TrendingUp, roles: ["owner", "manager", "supervisor"] },
    { to: "/planned/performance-review-29?title=Performance+Review&status=Direncanakan", label: "Performance Review", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/payroll?tab=payroll", label: "Payroll Run dan Penggajian", icon: Wallet, roles: ["owner", "manager", "keuangan"] },
    { to: "/payroll?tab=kasbon", label: "Kasbon Karyawan", icon: Wallet, roles: ["owner", "manager", "keuangan"] },
    { to: "/activity", label: "Riwayat Aktivitas Karyawan", icon: Activity },
  ]},
  { label: "Fasilitas", items: [
      ...plannedItems([
      ["kategori-aset", "Kategori Aset"],
      ["lokasi-aset", "Lokasi Aset"]
    ]),
    { to: "/master-catalog/scope-akses?title=Scope+Akses", label: "Scope Akses", icon: Building2, roles: ["owner"] },
    { to: "/master-catalog/penomoran-dokumen?title=Penomoran+Dokumen", label: "Penomoran Dokumen", icon: Building2, roles: ["owner"] },
    { to: "/master-catalog/template-dokumen?title=Template+Dokumen", label: "Template Dokumen", icon: Building2, roles: ["owner"] },
    { to: "/master-catalog/mata-uang?title=Mata+Uang", label: "Mata Uang", icon: Building2, roles: ["owner"] },
    { to: "/master-catalog/zona-waktu?title=Zona+Waktu", label: "Zona Waktu", icon: Building2, roles: ["owner"] },
    { to: "/master-catalog/preferensi-perusahaan?title=Preferensi+Perusahaan", label: "Preferensi Perusahaan", icon: Building2, roles: ["owner"] },
    { to: "/master-catalog/referensi-integrasi?title=Referensi+Integrasi", label: "Referensi Integrasi", icon: Building2, roles: ["owner"] },
    { to: "/planned/organisasi-dan-tenant-51?title=Organisasi+dan+Tenant&status=Perlu+verifikasi", label: "Organisasi dan Tenant", status: "Perlu verifikasi", icon: Building2 },
  ]},
  { label: "Pusat Data", items: [
    { to: "/master-data?category=organisasi", label: "Organisasi", icon: Building2, roles: ["owner"] },
    { to: "/master-data?category=item", label: "Barang", icon: Package, roles: ["owner"] },
    { to: "/master-data?category=supplier", label: "Supplier", icon: ShoppingBag, roles: ["owner"] },
    { to: "/master-data?category=pelanggan", label: "Pelanggan", icon: Store, roles: ["owner"] },
    { to: "/master-data?category=keuangan", label: "Keuangan", icon: Wallet, roles: ["owner"] },
    { to: "/master-data?category=sdm", label: "SDM", icon: UserCheck, roles: ["owner"] },
    { to: "/master-data?category=aset", label: "Aset", icon: Warehouse, roles: ["owner"] },
    { to: "/master-data?category=marketing", label: "Marketing", icon: TrendingUp, roles: ["owner"] },
    { to: "/master-data?category=pengguna", label: "Pengguna", icon: Shield, roles: ["owner"] },
    { to: "/master-data?category=konfigurasi", label: "Konfigurasi", icon: Settings2, roles: ["owner"] },
  ]},
  { label: "Pusat Laporan", items: [
      ...plannedItems([
      ["laporan-neraca-saldo", "Laporan Neraca Saldo"],
      ["laporan-workflow-approval", "Laporan Workflow dan Approval"],
      ["laporan-memo-internal", "Laporan Memo Internal"],
      ["laporan-sla-persetujuan", "Laporan SLA Persetujuan"],
      ["laporan-aktivitas-pengguna", "Laporan Aktivitas Pengguna"],
      ["laporan-payroll", "Laporan Payroll"],
      ["laporan-sdm", "Laporan SDM"],
      ["laporan-fasilitas-dan-aset", "Laporan Fasilitas dan Aset"]
      ]),
    { to: "/planned/laporan-memorize?title=Laporan+—+Memorize&status=Direncanakan", label: "Laporan — Memorize", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-keuangan?title=Laporan+—+Keuangan&status=Direncanakan", label: "Laporan — Keuangan", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-buku-besar?title=Laporan+—+Buku+Besar&status=Direncanakan", label: "Laporan — Buku Besar", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-kas-dan-bank?title=Laporan+—+Kas+&+Bank&status=Direncanakan", label: "Laporan — Kas & Bank", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-piutang?title=Laporan+—+Piutang&status=Direncanakan", label: "Laporan — Piutang", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-penjualan?title=Laporan+—+Penjualan&status=Direncanakan", label: "Laporan — Penjualan", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-tenaga-penjual?title=Laporan+—+Tenaga+Penjual&status=Direncanakan", label: "Laporan — Tenaga Penjual", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-utang?title=Laporan+—+Utang&status=Direncanakan", label: "Laporan — Utang", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-pembelian?title=Laporan+—+Pembelian&status=Direncanakan", label: "Laporan — Pembelian", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-persediaan?title=Laporan+—+Persediaan&status=Direncanakan", label: "Laporan — Persediaan", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-gudang?title=Laporan+—+Gudang&status=Direncanakan", label: "Laporan — Gudang", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-pekerjaan-pesanan?title=Laporan+—+Pekerjaan+Pesanan&status=Direncanakan", label: "Laporan — Pekerjaan Pesanan", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-manufaktur?title=Laporan+—+Manufaktur&status=Direncanakan", label: "Laporan — Manufaktur", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-aset-tetap?title=Laporan+—+Aset+Tetap&status=Direncanakan", label: "Laporan — Aset Tetap", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-pajak?title=Laporan+—+Pajak&status=Direncanakan", label: "Laporan — Pajak", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-pemeriksaan?title=Laporan+—+Pemeriksaan&status=Direncanakan", label: "Laporan — Pemeriksaan", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/planned/laporan-lain-lain?title=Laporan+—+Lain-lain&status=Direncanakan", label: "Laporan — Lain-lain", status: "Direncanakan", icon: FileBarChart2 },
    { to: "/finance?tab=daily", label: "Laporan Harian Keuangan", icon: BookOpen, roles: ["owner", "manager", "keuangan", "supervisor"] },
    { to: "/reports?tab=pnl", label: "Laporan Laba Rugi (P&L)", icon: FileBarChart2, roles: ["owner", "manager", "keuangan"] },
    { to: "/reports?tab=sales", label: "Laporan Penjualan", icon: TrendingUp, roles: ["owner", "manager", "keuangan"] },
    { to: "/reports?tab=cogs", label: "Laporan HPP / COGS", icon: Package, roles: ["owner", "manager", "keuangan"] },
    { to: "/planned/neraca-25?title=Neraca&status=Direncanakan", label: "Neraca", status: "Direncanakan", icon: Scale },
    { to: "/planned/laporan-arus-kas-26?title=Laporan+Arus+Kas&status=Perlu+verifikasi", label: "Laporan Arus Kas", status: "Perlu verifikasi", icon: TrendingUp },
  ]},
  { label: "Profil Pengguna", items: [
      ...plannedItems([
      ["memo-saya", "Memo Saya"],
      ["disposisi-dan-tanggapan-saya", "Disposisi dan Tanggapan Saya"],
      ["tugas-saya", "Tugas Saya"],
      ["riwayat-pengajuan-saya", "Riwayat Pengajuan Saya"]
      ]),
    { to: "/activity", label: "Beranda Saya", icon: LayoutDashboard },
    { to: "/planned/mulai-kerja-dan-selesai-kerja-55?title=Mulai+Kerja+dan+Selesai+Kerja&status=Perlu+verifikasi", label: "Mulai Kerja dan Selesai Kerja", status: "Perlu verifikasi", icon: Activity },
    { to: "/hr", label: "Absensi Saya", icon: UserCheck, roles: ["owner", "manager", "supervisor"] },
    { to: "/shifts", label: "Jadwal Saya", icon: Activity, roles: ["owner", "manager", "supervisor", "kasir", "cashier_outlet"] },
    { to: "/hr", label: "Tugas dan Jobdesk Saya", icon: ClipboardList, roles: ["owner", "manager", "supervisor"] },
    { to: "/hr", label: "KPI Saya", icon: TrendingUp, roles: ["owner", "manager", "supervisor"] },
    { to: "/payroll", label: "Pengajuan Saya", icon: ClipboardList, roles: ["owner", "manager", "keuangan"] },
    { to: "/planned/persetujuan-yang-menunggu-56?title=Persetujuan+yang+Menunggu&status=Direncanakan", label: "Persetujuan yang Menunggu", status: "Direncanakan", icon: ClipboardList },
    { to: "/planned/notifikasi-57?title=Notifikasi&status=Perlu+verifikasi", label: "Notifikasi", status: "Perlu verifikasi", icon: Activity },
    { to: "/activity", label: "Riwayat Aktivitas Saya", icon: Activity },
  ]},
  { label: "Pengaturan", items: [
      ...plannedItems([
      ["matriks-kewenangan", "Matriks Kewenangan"],
      ["template-workflow-approval", "Template Workflow Approval"],
      ["batas-nominal-persetujuan", "Batas Nominal Persetujuan"],
      ["delegasi-persetujuan", "Delegasi Persetujuan"],
      ["eskalasi-dan-sla-approval", "Eskalasi dan SLA Approval"],
      ["template-memo", "Template Memo"],
      ["aturan-penerima-dan-cc-memo", "Aturan Penerima dan CC Memo"],
      ["aturan-disposisi-memo", "Aturan Disposisi Memo"],
      ["prioritas-dan-kerahasiaan-memo", "Prioritas dan Kerahasiaan Memo"],
      ["pengaturan-notifikasi", "Pengaturan Notifikasi"]
      ]),
    { to: "/access-control", label: "Pengguna dan Role / Permission", icon: Shield, roles: ["owner"] },
    { to: "/configuration", label: "Konfigurasi Sistem", icon: Settings2, roles: ["owner", "manager"] },
    { to: "/planned/pengaturan-alur-persetujuan-52?title=Pengaturan+Alur+Persetujuan&status=Direncanakan", label: "Pengaturan Alur Persetujuan", status: "Direncanakan", icon: ClipboardList },
    { to: "/audit", label: "Audit Log", icon: Shield, roles: ["owner", "manager"] },
    { to: "/planned/system-health-53?title=System+Health&status=Perlu+verifikasi", label: "System Health", status: "Perlu verifikasi", icon: Activity },
    { to: "/planned/backup-dan-recovery-54?title=Backup+dan+Recovery&status=Perlu+verifikasi", label: "Backup dan Recovery", status: "Perlu verifikasi", icon: Shield },
  ]},
];

const NAV_GROUP_ICONS = {
  "Persiapan Data": Building2,
  "Pusat Informasi": LayoutDashboard,
  "POS Kasir": ShoppingCart,
  "Penjualan": Receipt,
  "Pembelian": ShoppingBag,
  "Persediaan": Warehouse,
  "Pemasaran": Tag,
  "Produksi": Layers,
  "Keuangan": Wallet,
  "Personalia": UserCheck,
  "Fasilitas": Shield,
  "Pusat Data": Store,
  "Pusat Laporan": FileBarChart2,
  "Profil Pengguna": UserCheck,
  "Pengaturan": Settings2,
};

const roleLabel = { owner: "Owner", manager: "Manager", supervisor: "Supervisor", kasir: "Kasir", gudang: "Warehouse", keuangan: "Finance" };

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [live, setLive] = useState(false);
  const [navSearch, setNavSearch] = useState("");
  const [sidebarConfig, setSidebarConfig] = useState({});
  useEffect(() => {
    let alive = true;
    api.get("/configuration?scope_type=tenant").then(({ data }) => {
      if (!alive) return;
      const item = (data.settings || []).find(x => x.key === "ui.sidebar");
      if (item?.value && typeof item.value === "object") setSidebarConfig(item.value);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);


  useRealtime((msg) => {
    setLive(true);
    if (msg.event === "transaction.paid") toast.success(`Transaksi lunas: Rp${(msg.data.total || 0).toLocaleString("id-ID")}`);
    else if (msg.event === "payment.paid") toast.info("QRIS Payment sukses");
    else if (msg.event === "shift.opened") toast.info("Shift dibuka");
  });

  const location = useLocation();
  const [workspaceTabs, setWorkspaceTabs] = useState([]);
  const [activeWorkspaceTabId, setActiveWorkspaceTabId] = useState(null);

  const getWorkspaceTabLabel = useCallback((pathname, search = "") => {
    const fullPath = pathname + search;
    const matchingItems = NAV_GROUPS.flatMap((group) => group.items)
      .filter((item) => item.to && (
        item.to.split("?")[0] === pathname && new URLSearchParams(item.to.split("?")[1] || "").toString() === new URLSearchParams(search).toString() ||
        (!item.to.includes("?") && item.to.split("?")[0] === pathname)
      ))
      .filter((item) => !item.roles || item.roles.includes(user?.role));
    return matchingItems[0]?.label || (pathname === "/dashboard" ? "Dashboard" : matchingItems[0]?.label || pathname);
  }, [user?.role]);

  useEffect(() => {
    const pathname = location.pathname;
    const fullPath = pathname + location.search;
    if (!pathname || pathname === "/login") return;

    const isOrgPath = (path) => {
      const base = (path || "").split("?")[0];
      return base === "/master-data" || base === "/outlets";
    };
    const isOrg = isOrgPath(pathname);

    setWorkspaceTabs((current) => {
      current = Array.isArray(current) ? current : [];

      if (isOrg) {
        const orgTabs = current.filter((tab) => isOrgPath(tab.path));
        const keep = orgTabs[0];

        const organizationTab = keep
          ? { ...keep, path: fullPath, label: "Organisasi" }
          : createWorkspaceTab(fullPath, "Organisasi");

        const otherTabs = current.filter((tab) => !isOrgPath(tab.path));

        setActiveWorkspaceTabId(organizationTab.id);
        return [...otherTabs, organizationTab];
      }

      const existing = current.find((tab) => tab.path === fullPath);
      if (existing) {
        setActiveWorkspaceTabId(existing.id);
        return current;
      }

      const nextTab = createWorkspaceTab(
        fullPath,
        getWorkspaceTabLabel(pathname, location.search)
      );

      setActiveWorkspaceTabId(nextTab.id);
      return [...current, nextTab];
    });
  }, [location.pathname, location.search, user?.role, getWorkspaceTabLabel]);

  const [expandedGroup, setExpandedGroup] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeFlyoutGroup, setActiveFlyoutGroup] = useState(null);

  useEffect(() => {
    const preferredGroup = { "/dashboard": "Pusat Informasi", "/shifts": "Personalia", "/outlets": "Pusat Data", "/activity": "Profil Pengguna", "/hr": "Profil Pengguna" };
    const activeGroup = NAV_GROUPS.find(group => group.label === preferredGroup[location.pathname]) || NAV_GROUPS.find(group => group.items.some(item => item.to && item.to.split("?")[0] === location.pathname));
    setExpandedGroup(activeGroup ? activeGroup.label : null);
  }, [location.pathname]);


  const menuGroups = useMemo(() => {
    const hidden = new Set(Array.isArray(sidebarConfig.hidden) ? sidebarConfig.hidden : []);
    const labels = sidebarConfig.labels || {};
    const query = navSearch.trim().toLowerCase();
    const basePath = (to) => to ? to.split("?")[0] : "";
    return NAV_GROUPS.map(group => {
      const items = group.items
        .filter(item => !item.roles || item.roles.includes(user?.role))
        .filter(item => !item.to || (!hidden.has(item.to) && !hidden.has(basePath(item.to))))
        .map(item => {
          const customLabel = labels[item.to];
          return {
            ...item,
            label: typeof customLabel === "string" && customLabel.trim()
              ? customLabel.trim()
              : item.label,
          };
        })
        .filter(item => !query || item.label.toLowerCase().includes(query) || group.label.toLowerCase().includes(query))

      return { ...group, items };
    }).filter(group => group.items.length > 0);
  }, [user?.role, navSearch, sidebarConfig]);

  return (
    <div className="h-screen overflow-hidden flex bg-slate-50">
  <aside data-testid="sidebar"
  className={`mazboy-sidebar fixed lg:sticky top-0 left-0 z-40 h-screen w-[46px] min-w-[46px] max-w-[46px] flex-[0_0_46px] ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 bg-[#123958] text-slate-100 flex flex-col`}>
  <div className="h-[68px] flex items-center justify-center border-b border-white/10 shrink-0">
    <button type="button" onClick={() => nav("/dashboard")} title="Mazboy Hub" className="w-9 h-9 rounded-lg bg-orange-500 text-white flex items-center justify-center"><Store className="w-5 h-5" /></button>
  </div>
  <nav aria-label="Kategori navigasi" className="flex-1 min-h-0 overflow-y-auto py-2">
    {menuGroups.map(group => {
      const Icon = NAV_GROUP_ICONS[group.label] || CircleHelp;
      const active = group.items.some(item => item.to && item.to.split("?")[0] === location.pathname);
      const selected = activeFlyoutGroup === group.label;
      return <button key={group.label} type="button" title={group.label} aria-label={group.label} aria-expanded={selected} onMouseEnter={() => setActiveFlyoutGroup(group.label)} onClick={() => setActiveFlyoutGroup(group.label)} className={`w-full h-11 flex items-center justify-center border-l-[3px] ${selected || active ? "bg-[#285a80] border-orange-500 text-white" : "border-transparent text-slate-200 hover:bg-[#214d70]"}`}><Icon className="w-[24px] h-[24px]" /></button>;
    })}
  </nav>
  <div className="h-12 flex items-center justify-center border-t border-white/10"><div title={user?.name || "Pengguna"} className="w-8 h-8 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">{user?.name?.[0] || "M"}</div></div>
  <button type="button" title="Keluar" onClick={() => { logout(); nav("/login"); }} className="h-10 flex items-center justify-center text-slate-300 hover:bg-slate-800"><LogOut className="w-4 h-4" /></button>
</aside>
{activeFlyoutGroup && <section onMouseLeave={() => setActiveFlyoutGroup(null)} className="mazboy-flyout-nav fixed left-[44px] top-[90px] z-[60] w-[433px] max-h-[calc(100vh-110px)] max-w-[calc(100vw-45px)] bg-white border border-slate-300 rounded-r shadow-2xl flex flex-col">
  <div className="h-[54px] px-4 flex items-center justify-between border-b border-rose-400 shrink-0"><h2 className="text-[17px] font-medium text-slate-700">{activeFlyoutGroup}</h2><button type="button" onClick={() => setActiveFlyoutGroup(null)} className="text-xl">×</button></div>

  <nav className="flex-1 min-h-0 overflow-y-auto p-2"><div className="grid grid-cols-[repeat(auto-fit,minmax(76px,1fr))] gap-2">
    {menuGroups.find(g => g.label === activeFlyoutGroup)?.items.filter(item => !navSearch.trim() || item.label.toLowerCase().includes(navSearch.trim().toLowerCase())).map((item, i) => {
      const Icon = item.icon || CircleHelp;
      return item.to ? <NavLink key={item.to} to={item.to} onClick={() => {setActiveFlyoutGroup(null);setNavSearch("");setOpen(false);}} className="h-[72px] w-full border border-sky-400 rounded bg-sky-100 text-sky-800 flex flex-col items-center justify-center gap-2 p-2 text-center"><Icon className="w-6 h-6"/><span className="text-[11px]">{item.label}</span></NavLink> : <div key={item.label+i} className="h-[72px] w-full border rounded flex flex-col items-center justify-center gap-2 p-2 text-center text-slate-400"><Icon className="w-6 h-6"/><span className="text-[11px]">{item.label}</span></div>;
    })}
  </div></nav>

 </section>}

      {open && <div onClick={()=>setOpen(false)} className="fixed inset-0 z-30 bg-slate-950/60 backdrop-blur-sm lg:hidden"/>}

      <div className="flex-1 flex flex-col min-w-0">
        <header aria-label="Header utama Mazboy Hub" className="h-10 bg-white border-b border-slate-300 sticky top-0 z-20 flex items-center justify-between px-3 sm:px-5 lg:px-7">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="lg:hidden shrink-0" onClick={() => setOpen(true)}><Menu className="w-5 h-5"/></Button>
            <div className="min-w-0"><div className="text-xs text-slate-400 hidden sm:block">MAZBOY HUB</div><div className="text-sm font-semibold text-slate-800 truncate">Selamat datang, {user?.name}</div></div>
          </div>
          <div className="flex items-center gap-2">
            <Badge role="status" aria-live="polite" title={live ? "Koneksi realtime terdeteksi" : "Menunggu aktivitas realtime"} variant="outline" className={`gap-1.5 ${live ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-slate-200 text-slate-500"}`}><span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`}/>{live ? "Realtime" : "Menghubungkan..."}</Badge>
            <Button variant="ghost" size="icon" className="hidden sm:inline-flex" title="Bantuan"><CircleHelp className="w-4 h-4 text-slate-500"/></Button>
          </div>
        </header>
        <WorkspaceTabs
          pinnedTabs={user?.role === "owner" ? [{
            id: "company-setup-pinned",
            path: "/company-setup",
            label: "Persiapan Data Perusahaan",
            closable: false,
          }] : []}
          tabs={workspaceTabs}
          onActivate={(tab) => setActiveWorkspaceTabId(tab.id)}
          onClose={(tabId) => {
            const targetTab = workspaceTabs.find((tab) => tab.id === tabId);
            if (!targetTab || !targetTab.closable) return;

            const path = targetTab.path;
            const activePath = location.pathname + location.search;
            const isActiveTab = activePath === path;

            if (isActiveTab) {
              const tabIndex = workspaceTabs.findIndex((tab) => tab.id === tabId);
              const remaining = workspaceTabs.filter((tab) => tab.id !== tabId);
              const nextTab =
                remaining[tabIndex] ||
                remaining[tabIndex - 1] ||
                remaining.find((tab) => tab.path === "/dashboard");

              setActiveWorkspaceTabId(nextTab?.id || null);
              nav(nextTab?.path || "/dashboard");
            }

            setWorkspaceTabs((current) =>
              removeWorkspaceTab(current, tabId)
            );
          }}
        />

{/* MAZBOY_NAVIGATION_SHELL_PATCH_2 */}
<div
  id="mazboy-navigation-layer-2"
  data-mazboy-navigation-slot="2"
  aria-label="Navigasi workspace"
  className="mz-navigation-slot mz-navigation-slot-2"
/>
<div
  id="mazboy-navigation-layer-3"
  data-mazboy-navigation-slot="3"
  aria-label="Navigasi fungsi halaman"
  className="mz-navigation-slot mz-navigation-slot-3"
/>

        <main data-testid="main-content" className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain"><PersistentWorkspaceOutlet
          tabs={[
            ...workspaceTabs,
            ...(user?.role === "owner"
              ? [{ path: "/company-setup", label: "Persiapan Data Perusahaan", closable: false }]
              : []),
          ]}
        /></main>
      </div>
    </div>
  );
}
